import { useEffect, useState } from 'react';
import styled from 'styled-components';
import incidentIoSrc from '@elementary/common-ui/assets/incident-io-full.svg';
import opsgenieSrc from '@elementary/common-ui/assets/opsgenie-full.svg';
import pagerDutySrc from '@elementary/common-ui/assets/pagerduty-full.svg';
import rootlySrc from '@elementary/common-ui/assets/rootly-full.svg';
import { Button, Flex } from '@elementary/common-ui/components';
import {
  FetchIncidentIoApi,
  FetchOpsgenieApi,
  FetchPagerDutyApi,
  FetchRootlyApi,
  IncidentIoIntegrationForm,
  IncidentManagementTool,
  IntegrationType,
  OpsgenieIntegrationForm,
  RootlyIntegrationForm,
} from 'shared/types';
import { space } from '@elementary/common-ui/style/utils';
import { IntegrationError } from '../../../../types';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import IncidentIoDialog from './IncidentIoDialog';
import OpsgenieDialog from './OpsgenieDialog';
import PagerDutyDialog from './PagerDutyDialog';
import RootlyDialog from './RootlyDialog';

const ProviderButton = styled(Button)`
  && {
    display: flex;
    height: 120px;
    padding: ${space('xxl')};
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    min-width: 0;
  }

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const ProviderSelection = ({
  onSelected,
}: {
  onSelected: (incidentManagementTool: IncidentManagementTool) => void;
}) => (
  <Flex gap={3}>
    <ProviderButton variant="outlined" onClick={() => onSelected('PagerDuty')}>
      <img src={pagerDutySrc} alt="PagerDuty" />
    </ProviderButton>
    <ProviderButton variant="outlined" onClick={() => onSelected('Opsgenie')}>
      <img src={opsgenieSrc} alt="Opsgenie" />
    </ProviderButton>
    <ProviderButton variant="outlined" onClick={() => onSelected('IncidentIo')}>
      <img src={incidentIoSrc} alt="incident.io" />
    </ProviderButton>
    <ProviderButton variant="outlined" onClick={() => onSelected('Rootly')}>
      <img src={rootlySrc} alt="Rootly" />
    </ProviderButton>
  </Flex>
);

type Props = {
  open: boolean;
  onClose: () => void;
  data?: {
    pagerduty?: FetchPagerDutyApi;
    opsgenie?: FetchOpsgenieApi;
    incidentIo?: FetchIncidentIoApi;
    rootly?: FetchRootlyApi;
  };
  onPagerDutyIntegrationSubmit: () => void;
  isPagerDutyIntegrationLoading?: boolean;
  pagerDutySetIntegrationError?: IntegrationError;
  onOpsgenieIntegrationSubmit: (data: OpsgenieIntegrationForm) => void;
  opsgenieSetIntegrationError?: IntegrationError;
  opsgenieLoading?: boolean;
  onIncidentIoIntegrationSubmit: (data: IncidentIoIntegrationForm) => void;
  incidentIoSetIntegrationError?: IntegrationError;
  incidentIoLoading?: boolean;
  onRootlyIntegrationSubmit: (data: RootlyIntegrationForm) => void;
  rootlySetIntegrationError?: IntegrationError;
  rootlyLoading?: boolean;
  error?: IntegrationError;
  onDisconnect?: (integrationType: IntegrationType) => void;
};

const IncidentManagementToolDialog = ({
  open,
  onClose,
  data,
  onPagerDutyIntegrationSubmit,
  isPagerDutyIntegrationLoading,
  pagerDutySetIntegrationError,
  onOpsgenieIntegrationSubmit,
  opsgenieSetIntegrationError,
  opsgenieLoading,
  onIncidentIoIntegrationSubmit,
  incidentIoSetIntegrationError,
  incidentIoLoading,
  onRootlyIntegrationSubmit,
  rootlySetIntegrationError,
  rootlyLoading,
  error,
  onDisconnect,
}: Props) => {
  const [userSelectedTool, setUserSelectedTool] =
    useState<IncidentManagementTool>();

  useEffect(() => {
    if (open === false) {
      setUserSelectedTool(undefined);
    }
  }, [open]);

  const selectedTool = data?.pagerduty?.integrated
    ? 'PagerDuty'
    : data?.opsgenie?.integrated
    ? 'Opsgenie'
    : data?.incidentIo?.integrated
    ? 'IncidentIo'
    : data?.rootly?.integrated
    ? 'Rootly'
    : userSelectedTool;

  const isEdit =
    data?.pagerduty?.integrated ||
    data?.opsgenie?.integrated ||
    data?.incidentIo?.integrated ||
    data?.rootly?.integrated;

  return (
    <>
      {!selectedTool && (
        <IntegrationDialog
          open={open}
          onClose={onClose}
          title="Connect your incident management tool"
          hideSaveButton
          error={
            error ||
            pagerDutySetIntegrationError ||
            opsgenieSetIntegrationError ||
            incidentIoSetIntegrationError ||
            rootlySetIntegrationError
          }
        >
          <ProviderSelection
            onSelected={(value) => setUserSelectedTool(value)}
          />
        </IntegrationDialog>
      )}

      {selectedTool === 'PagerDuty' && (
        <PagerDutyDialog
          open={open}
          onClose={onClose}
          data={data?.pagerduty}
          onPagerDutyIntegrationSubmit={onPagerDutyIntegrationSubmit}
          isPagerDutyIntegrationLoading={isPagerDutyIntegrationLoading}
          error={error || pagerDutySetIntegrationError}
          edit={isEdit}
          onDisconnect={
            isEdit && onDisconnect
              ? () => onDisconnect('pager_duty')
              : undefined
          }
        />
      )}

      {selectedTool === 'Opsgenie' && (
        <OpsgenieDialog
          open={open}
          onClose={onClose}
          data={data?.opsgenie}
          onOpsgenieIntegrationSubmit={onOpsgenieIntegrationSubmit}
          error={error || opsgenieSetIntegrationError}
          loading={opsgenieLoading}
          onDisconnect={
            data?.opsgenie?.integrated && onDisconnect
              ? () => onDisconnect('opsgenie')
              : undefined
          }
        />
      )}

      {selectedTool === 'IncidentIo' && (
        <IncidentIoDialog
          open={open}
          onClose={onClose}
          data={data?.incidentIo}
          onIncidentIoIntegrationSubmit={onIncidentIoIntegrationSubmit}
          error={error || incidentIoSetIntegrationError}
          loading={incidentIoLoading}
          onDisconnect={
            data?.incidentIo?.integrated && onDisconnect
              ? () => onDisconnect('incident_io')
              : undefined
          }
        />
      )}

      {selectedTool === 'Rootly' && (
        <RootlyDialog
          open={open}
          onClose={onClose}
          data={data?.rootly}
          onRootlyIntegrationSubmit={onRootlyIntegrationSubmit}
          error={error || rootlySetIntegrationError}
          loading={rootlyLoading}
          onDisconnect={
            data?.rootly?.integrated && onDisconnect
              ? () => onDisconnect('rootly')
              : undefined
          }
        />
      )}
    </>
  );
};

export default IncidentManagementToolDialog;
